import { useTranslation } from 'next-i18next';
import cx from 'classnames';
import { Link } from '@dx-ui/osc-link';

import Icon from '@dx-ui/osc-icon';
import { HEADER_CLICK_IDS } from '../types';
import type { HeaderProps, TDrawerLink } from '../types';
import {
  dispatchGlobalNavigationClick,
  dispatchGlobalNavigationClickBasedOnLabel,
} from '../header.utilities';
import { getLocaleFormattedNumber, replaceLang } from '@dx-ui/framework-i18n';
import { useRouter } from 'next/router';

export function NavigationPopupContents({
  subMenu,
  label,
  onGlobalNavigationClick,
  isLegacyGlobalNavigationClickAnalyticsEnabled = true,
}: {
  subMenu: TDrawerLink[];
  label: string;
  onGlobalNavigationClick?: HeaderProps['onGlobalNavigationClick'];
  isLegacyGlobalNavigationClickAnalyticsEnabled?: HeaderProps['isLegacyGlobalNavigationClickAnalyticsEnabled'];
}) {
  const { locale = 'en' } = useRouter();

  return (
    <div className="container flex-1 py-5">
      <MenuList>
        {subMenu.map(
          ({ label: subMenuLabel, link: { experimentationConfiguration, ...link }, onClick }) => (
            <MenuListItem key={subMenuLabel}>
              <Link
                {...link}
                url={replaceLang(link?.url, locale)}
                data-conductrics-goal={experimentationConfiguration?.goal}
                data-conductrics-value={experimentationConfiguration?.value}
                underline={false}
                className="osc-nav-drawer-link-text"
                anchorClassName="osc-nav-drawer-link p-3 hover:osc-nav-drawer-link-active focus:osc-nav-drawer-link-active brand-ey:focus:font-normal block"
                onClick={(event) => {
                  dispatchGlobalNavigationClickBasedOnLabel(
                    subMenuLabel,
                    label,
                    onGlobalNavigationClick,
                    isLegacyGlobalNavigationClickAnalyticsEnabled
                  );
                  onClick?.();
                  link?.onClick?.(event);
                }}
              >
                {subMenuLabel}
              </Link>
            </MenuListItem>
          )
        )}
      </MenuList>
    </div>
  );
}

export type UserPopupContentsProps = Pick<
  HeaderProps,
  | 'userLinks'
  | 'shouldOpenAccountLinksInNewWindow'
  | 'shouldSuppressHonorsAccountLinks'
  | 'onSignOut'
  | 'onGlobalNavigationClick'
  | 'isLegacyGlobalNavigationClickAnalyticsEnabled'
> & {
  user: Exclude<HeaderProps['user'], undefined>;
};

export default function UserPopupContents({
  user,
  userLinks,
  shouldOpenAccountLinksInNewWindow,
  shouldSuppressHonorsAccountLinks,
  onSignOut,
  onGlobalNavigationClick,
  isLegacyGlobalNavigationClickAnalyticsEnabled = true,
}: UserPopupContentsProps) {
  const { locale = 'en' } = useRouter();
  const [t] = useTranslation('osc-header');

  const formattedHonorsPointsTotal = getLocaleFormattedNumber(user?.honorsPoints, { locale });

  return (
    <div className="container flex py-5">
      <div className="flex items-center">
        <div className="flex size-20 shrink-0 items-center justify-center rounded-full bg-hilton-alt">
          <Icon name="user" size="4xl" />
        </div>
        <div className="ms-5">
          <p className="text--sm mb-1 font-bold brand-ey:font-normal">
            {t('greetings', { username: user.name })}
          </p>
          <p className="mb-1 capitalize">
            <span className="text--sm font-bold underline brand-ey:font-normal">
              {user.honorsTier}
            </span>{' '}
            {t('status')}
          </p>
          <p className="text--sm mb-1 brand-ey:font-normal">
            {t('honorsPointsTotal', { honorsPointsTotal: formattedHonorsPointsTotal })}
          </p>
          {user.hhonorsNumber ? (
            <p className="text--sm mb-1 brand-ey:font-normal">
              {t('hiltonHonorsNumber', { hhonorsNumber: user.hhonorsNumber })}
            </p>
          ) : null}
        </div>
      </div>
      <div className="ms-5 flex-1 xl:ms-10">
        <MenuList>
          {userLinks?.accountLink ? (
            <MegaUserLink
              url={userLinks?.accountLink.url}
              onClick={() => {
                dispatchGlobalNavigationClick(
                  HEADER_CLICK_IDS.MemberNameHiltonHonorsHome,
                  onGlobalNavigationClick,
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                );
              }}
              isNewWindow={shouldOpenAccountLinksInNewWindow}
            >
              {t('hiltonHonorsHome')}
            </MegaUserLink>
          ) : null}
          {userLinks?.honorsActivityLink && !shouldSuppressHonorsAccountLinks ? (
            <MegaUserLink
              url={userLinks?.honorsActivityLink.url}
              onClick={() => {
                dispatchGlobalNavigationClick(
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                    ? HEADER_CLICK_IDS.MemberNameHiltonHonorsActivity
                    : HEADER_CLICK_IDS.MemberNameActivity,
                  onGlobalNavigationClick,
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                );
              }}
              isNewWindow={shouldOpenAccountLinksInNewWindow}
            >
              {t('activity')}
            </MegaUserLink>
          ) : null}

          {userLinks?.honorsPointsLink?.url && !shouldSuppressHonorsAccountLinks ? (
            <MegaUserLink
              url={userLinks.honorsPointsLink.url}
              onClick={() => {
                dispatchGlobalNavigationClick(
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                    ? HEADER_CLICK_IDS.MemberNameHiltonHonorsPoints
                    : HEADER_CLICK_IDS.MemberNamePoints,
                  onGlobalNavigationClick,
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                );
              }}
              isNewWindow={shouldOpenAccountLinksInNewWindow}
            >
              {t('pointsLabel')}
            </MegaUserLink>
          ) : null}

          {userLinks?.honorsProfileLink?.url && !shouldSuppressHonorsAccountLinks ? (
            <MegaUserLink
              url={userLinks.honorsProfileLink.url}
              onClick={() => {
                dispatchGlobalNavigationClick(
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                    ? HEADER_CLICK_IDS.MemberNameHiltonHonorsProfile
                    : HEADER_CLICK_IDS.MemberNameProfile,
                  onGlobalNavigationClick,
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                );
              }}
              isNewWindow={shouldOpenAccountLinksInNewWindow}
            >
              {t('profile')}
            </MegaUserLink>
          ) : null}

          {userLinks?.yourOffersLink?.url && !shouldSuppressHonorsAccountLinks ? (
            <MegaUserLink
              url={userLinks.yourOffersLink.url}
              onClick={() => {
                dispatchGlobalNavigationClick(
                  HEADER_CLICK_IDS.MemberNameHiltonHonorsYourOffers,
                  onGlobalNavigationClick,
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                );
              }}
              isNewWindow={shouldOpenAccountLinksInNewWindow}
            >
              {t('yourOffers')}
            </MegaUserLink>
          ) : null}

          {userLinks?.goHiltonLink?.url ? (
            <MegaUserLink
              url={userLinks.goHiltonLink.url}
              onClick={() => {
                dispatchGlobalNavigationClick(
                  HEADER_CLICK_IDS.MemberNameGoHiltonProgramDetails,
                  onGlobalNavigationClick,
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                );
              }}
              isNewWindow={shouldOpenAccountLinksInNewWindow}
            >
              {t('goHiltonProgramDetails')}
            </MegaUserLink>
          ) : null}

          {userLinks?.friendsAndFamilyLink?.url ? (
            <MegaUserLink
              url={userLinks.friendsAndFamilyLink.url}
              onClick={() => {
                dispatchGlobalNavigationClick(
                  HEADER_CLICK_IDS.MemberNameManageFriendsAndFamily,
                  onGlobalNavigationClick,
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                );
              }}
              isNewWindow={shouldOpenAccountLinksInNewWindow}
            >
              {t('manageFriendsAndFamily')}
            </MegaUserLink>
          ) : null}

          {userLinks?.hgvMaxLink?.url ? (
            <MegaUserLink
              url={userLinks.hgvMaxLink.url}
              onClick={() => {
                dispatchGlobalNavigationClick(
                  HEADER_CLICK_IDS.MemberNameHgvMaxRateDetails,
                  onGlobalNavigationClick,
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                );
              }}
              isNewWindow={shouldOpenAccountLinksInNewWindow}
            >
              {t('hgvMaxRateDetails')}
            </MegaUserLink>
          ) : null}

          {userLinks?.b2bMemberLink?.url ? (
            <MegaUserLink
              url={userLinks.b2bMemberLink.url}
              onClick={() => {
                dispatchGlobalNavigationClick(
                  HEADER_CLICK_IDS.MemberNameManageHiltonForBusiness,
                  onGlobalNavigationClick,
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                );
              }}
              isNewWindow={shouldOpenAccountLinksInNewWindow}
            >
              {userLinks?.b2bMemberLink?.label}
            </MegaUserLink>
          ) : null}
          <MenuListItem>
            <button
              className="osc-nav-drawer-link osc-nav-drawer-link-text hover:osc-nav-drawer-link-active focus:osc-nav-drawer-link-active size-full whitespace-nowrap py-3 text-left"
              onClick={async () => {
                dispatchGlobalNavigationClick(
                  HEADER_CLICK_IDS.MemberNameSignOut,
                  onGlobalNavigationClick,
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                );
                await onSignOut();
              }}
              type="button"
            >
              {t('signOut')}
            </button>
          </MenuListItem>
        </MenuList>
      </div>
    </div>
  );
}

function MenuList({ children, ...listProps }: React.ComponentProps<'ul'>) {
  return (
    <ul {...listProps} className={cx('grid grid-cols-3 gap-x-6', listProps.className)}>
      {children}
    </ul>
  );
}

function MenuListItem({ children, ...listItemProps }: React.ComponentProps<'li'>) {
  return (
    <li {...listItemProps} data-testid="menu-list-item" className="py-3">
      <span className="block w-full border-b-2 border-surface hover:font-bold">{children}</span>
    </li>
  );
}

function MegaUserLink({
  url,
  onClick,
  children,
  isNewWindow,
}: {
  url: string;
  onClick: () => void;
  children: React.ReactNode;
  isNewWindow?: boolean;
}) {
  return (
    <MenuListItem>
      <Link
        url={url}
        underline={false}
        className="osc-nav-drawer-link-text whitespace-nowrap"
        anchorClassName="osc-nav-drawer-link hover:osc-nav-drawer-link-active focus:osc-nav-drawer-link-active py-3 size-full block"
        onClick={onClick}
        isNewWindow={isNewWindow}
      >
        {children}
      </Link>
    </MenuListItem>
  );
}
